// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import _ from 'lodash';
import moment from 'moment';
import helpers from '@/utils/helpers';
import { buildUserProfileMaps } from '@/utils/userProfileLookup';

import useTeamDivisionStore from '@store/teamDivisionStore';
import usePlayerStore from '@store/playerStore';
import useRegistrationStore from '@store/registrationStore';
import useOrganizationStore from '@store/organizationStore';
import useRegistrationTeamStore from '@store/registrationTeamStore';
import usePaymentStore from '@store/paymentStore';
import useDivisionStore from '@store/divisionStore';
import useUserProfileStore from '@store/userProfileStore';
import RegistrationTeamDetails from '@/components/RegistrationTeamDetails/RegistrationTeamDetails';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { PageHeaderSkeleton, ListSkeleton } from '@/components/Page/PageSkeletons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Save, Send } from 'lucide-react';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import { DEFAULT_MANAGER_FIELDS, COACH_TITLE_OPTIONS } from '@staty/shared/types';
import TeamPlayerActions from '@/components/TeamPlayerActions/TeamPlayerActions';
import PlayerEditDialog from '@/components/PlayerEditDialog/PlayerEditDialog';
import PlayerRemoveDialog from '@/components/PlayerRemoveDialog/PlayerRemoveDialog';

// Mirror of apps/app/app/components/RegistrationTournament/FormField.tsx:10-19
const formatPhoneNumber = (value: string) => {
  const phoneNumber = (value || '').replace(/\D/g, '');
  if (phoneNumber.length === 0) return '';
  if (phoneNumber.length <= 3) return `(${phoneNumber}`;
  if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

function RegistrationFieldInput({ field, value, onChange, invalid }: { field: any; value: any; onChange: (v: any) => void; invalid?: boolean }) {
  const errorClass = invalid ? 'border-destructive focus-visible:ring-destructive' : '';
  if (field.fieldType === 'select') {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className={errorClass}>
          <SelectValue placeholder={`Select ${field.name}`} />
        </SelectTrigger>
        <SelectContent>
          {(field.options || []).map((opt: string) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.fieldType === 'textarea') {
    return (
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.helperText || field.name}
        className={errorClass}
      />
    );
  }
  if (field.fieldType === 'phone') {
    return (
      <Input
        type="tel"
        value={value || ''}
        onChange={(e) => onChange(formatPhoneNumber(e.target.value))}
        placeholder={field.helperText || '(XXX) XXX-XXXX'}
        maxLength={14}
        className={errorClass}
      />
    );
  }
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.helperText || field.name}
      className={errorClass}
    />
  );
}

const BASE_MANAGER_FIELDS = ['first_name', 'last_name', 'email', 'phone', 'title'];

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const getTeamDivisionById = useTeamDivisionStore((state: any) => state.getTeamDivisionById);
  const updateTeamDivisionFn = useTeamDivisionStore((state: any) => state.updateTeamDivision);
  const updateTeamDivision = useMemo(() => updateTeamDivisionFn(getToken), [updateTeamDivisionFn, getToken]);
  const inviteManagerFn = useTeamDivisionStore((state: any) => state.inviteManager);
  const inviteManager = useMemo(() => inviteManagerFn(getToken), [inviteManagerFn, getToken]);
  const sendFinishRegistrationEmailsFn = useTeamDivisionStore((state: any) => state.sendFinishRegistrationEmails);
  const sendFinishRegistrationEmails = useMemo(() => sendFinishRegistrationEmailsFn(getToken), [sendFinishRegistrationEmailsFn, getToken]);
  const teamDivisions = useTeamDivisionStore((state: any) => state.teamDivisions);

  const updateTeamRegistrationFn = useRegistrationTeamStore((state: any) => state.updateTeamRegistration);

  const getPlayersByTeamId = usePlayerStore((state: any) => state.getPlayersByTeamId);
  const teamPlayers = usePlayerStore((state: any) => state.players);

  const { getRegistrationByTournamentId, getRegistrationByLeagueId, getRegistrationById, registrations } = useRegistrationStore((state: any) => ({
    getRegistrationByTournamentId: state.getRegistrationByTournamentId,
    getRegistrationByLeagueId: state.getRegistrationByLeagueId,
    getRegistrationById: state.getRegistrationById,
    registrations: state.registrations,
  }));
  const { organizations: orgLeagues } = useOrganizationStore((state: any) => ({
    organizations: state.organizations,
  }));

  // Enriched registrationTeam data for the read-only Registration Details card —
  // same sources /admin/registration/:id uses (findAllByRegistrationId +
  // payments + divisions), so payment/capacity/player detail matches exactly.
  const registrationTeams = useRegistrationTeamStore((state: any) => state.registrationTeams);
  const getRegistrationTeamsByRegistrationId = useRegistrationTeamStore((state: any) => state.getRegistrationTeamsByRegistrationId);
  const payments = usePaymentStore((state: any) => state.payments);
  const getPaymentsByRegistrationId = usePaymentStore((state: any) => state.getPaymentsByRegistrationId);
  const paymentPlans = usePaymentStore((state: any) => state.paymentPlans);
  const getPaymentPlansByRegistrationId = usePaymentStore((state: any) => state.getPaymentPlansByRegistrationId);
  const registrationDivisions = useDivisionStore((state: any) => state.divisions);
  const getDivisionsByRegistrationId = useDivisionStore((state: any) => state.getDivisionsByRegistrationId);
  // Org profile fields + per-user profiles for the "Profile: …" player columns
  // (same source /admin/registration/:id uses).
  const userProfiles = useUserProfileStore((state: any) => state.userProfiles);
  const profileFields = useUserProfileStore((state: any) => state.profileFields);
  const getUserProfilesByOrganization = useUserProfileStore((state: any) => state.getUserProfilesByOrganization);

  const [loading, setLoading] = useState(true);
  const [teamForm, setTeamForm] = useState({ name: '', active: true });
  const [coachForms, setCoachForms] = useState<any[]>([]);
  // RegistrationTeam admin fields (payment override + sync flag), edited via the
  // same "Save Team" button. customPaidAmount is in DOLLARS (matches the
  // registration Edit Team dialog; the *100 to cents happens downstream).
  const [regTeamForm, setRegTeamForm] = useState<{
    hasPaid: boolean; customPaidAmount: string; paidNotes: string; customPaidDate: any; disableSync: boolean;
  }>({ hasPaid: false, customPaidAmount: '', paidNotes: '', customPaidDate: null, disableSync: false });
  const [isSavingTeam, setIsSavingTeam] = useState(false);
  const [coachErrors, setCoachErrors] = useState<Set<string>>(new Set());
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  const team = teamDivisions.find((t) => t._id === teamId);
  const tournamentId = team ? helpers.getObjId(team?.league?.tournament) : null;
  const leagueId = team ? helpers.getObjId(team?.league) : null;
  const contestType = tournamentId ? 'tournament' : 'contest';
  const scheduleHref = tournamentId
    ? `/admin/organization/tournament/${tournamentId}/contest/${leagueId}/divisions/schedule`
    : leagueId
    ? `/admin/organization/league/${leagueId}/divisions/schedule`
    : null;
  // Resolve the registration from the team's OWN registrationTeam.registration —
  // a tournament/contest can have multiple registrations, so matching by
  // tournament/contest can pick the wrong one (and then regTeam never resolves).
  // Fall back to tournament/contest matching when the ref isn't available yet.
  const regTeamRegistrationId = helpers.getObjId(team?.registrationTeam?.registration);
  const registration = regTeamRegistrationId
    ? _.find(registrations, (r: any) => helpers.getObjId(r) === regTeamRegistrationId)
    : tournamentId
    ? _.find(registrations, (r: any) => helpers.getObjId(r.tournament) === tournamentId)
    : _.find(registrations, (r: any) => helpers.getObjId(r.contest) === leagueId);
  const organization = team?.organization
    ? (typeof team.organization === 'object' ? team.organization : _.find(orgLeagues, { _id: helpers.getObjId(team.organization) }))
    : null;

  useEffect(() => {
    if (!teamId) return;
    const loadData = async () => {
      setLoading(true);
      await getTeamDivisionById(teamId, { include: 'players,league,registrationTeam' });
      await getPlayersByTeamId(teamId);
      setLoading(false);
    };
    loadData();
  }, [teamId]);

  useEffect(() => {
    if (!team) return;
    // Prefer loading the team's own registration by id (correct even when the
    // tournament has multiple registrations); fall back to tournament/contest.
    if (regTeamRegistrationId) {
      getRegistrationById(regTeamRegistrationId);
    } else if (tournamentId) {
      getRegistrationByTournamentId(tournamentId);
    } else if (leagueId) {
      getRegistrationByLeagueId(leagueId);
    }
  }, [team?._id, regTeamRegistrationId, tournamentId, leagueId]);

  // Load the org's profile fields + per-user profiles (drives the "Profile: …"
  // player columns in the Registration Details card).
  useEffect(() => {
    if (organization?.name) getUserProfilesByOrganization(organization.name);
  }, [organization?.name]);

  const profileMaps = useMemo(() => buildUserProfileMaps(userProfiles || []), [userProfiles]);
  const userProfileByUser = profileMaps.byUser;

  // Once the registration is known, load the enriched teams + payments +
  // divisions that back the Registration Details card. Reused as a refresh after
  // creating/inviting a player so the read-only roster updates.
  const registrationId = registration ? helpers.getObjId(registration) : null;
  const refreshRegistrationData = useCallback(async () => {
    if (!registrationId) return;
    const token = await getToken();
    await Promise.all([
      getRegistrationTeamsByRegistrationId(registrationId, token),
      getPaymentsByRegistrationId(registrationId, token),
      getPaymentPlansByRegistrationId(registrationId, token),
      getDivisionsByRegistrationId(registrationId),
    ]);
    await getPlayersByTeamId(teamId);
  }, [registrationId, teamId]);
  useEffect(() => {
    refreshRegistrationData();
  }, [registrationId]);

  const regTeamId = team ? helpers.getObjId(team.registrationTeam) : null;
  // Roster for the manual-team card below — the store list is global (also
  // written by other pages), so pin it to THIS team's players.
  const rosterPlayers = useMemo(
    () => (teamPlayers || []).filter((p: any) => helpers.getObjId(p.team) === teamId),
    [teamPlayers, teamId],
  );
  const regTeam = regTeamId
    ? _.find(registrationTeams, (rt: any) => helpers.getObjId(rt._id) === regTeamId)
    : null;

  // Manager email for the registration invite: the owner's account email once
  // they've signed up, else the email captured when the invite was created.
  const managerEmail = _.get(regTeam || team?.registrationTeam, 'user.email')
    || _.get(regTeam || team?.registrationTeam, 'teamManagers[0].email')
    || '';
  const openResendDialog = () => {
    setResendEmail(managerEmail);
    setResendOpen(true);
  };

  const handleResendInvite = async () => {
    if (!team?._id) return;
    setIsResending(true);
    try {
      await inviteManager(helpers.getObjId(team), { email: resendEmail.trim() });
      setResendOpen(false);
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    if (!team) return;
    setTeamForm({
      name: team.name || '',
      active: team.active ?? true,
    });
    const teamManagers = team.registrationTeam?.teamManagers || [];
    setCoachForms(teamManagers.map((m: any) => ({ ...m, info: m.info || {} })));
    const rt = team.registrationTeam || {};
    setRegTeamForm({
      hasPaid: !!rt.hasPaid,
      customPaidAmount: rt.customPaidAmount != null ? String(rt.customPaidAmount) : '',
      paidNotes: rt.paidNotes || '',
      customPaidDate: rt.customPaidDate || null,
      disableSync: !!_.get(rt, 'config.disableSync'),
    });
  }, [team?._id, team?.registrationTeam?._id, team?.registrationTeam?.teamManagers]);

  const managerFieldsSchema = registration?.managerFields?.length ? registration.managerFields : DEFAULT_MANAGER_FIELDS;
  const customManagerFields = managerFieldsSchema.filter(
    (f: any) => f.include !== false && !BASE_MANAGER_FIELDS.includes(f.fieldId)
  );

  const validateCoaches = () => {
    const errors = new Set<string>();
    const missingLabels: string[] = [];
    coachForms.forEach((coach, idx) => {
      BASE_MANAGER_FIELDS.forEach((fieldId) => {
        const value = coach[fieldId];
        if (!value || !String(value).trim()) {
          errors.add(`${idx}.${fieldId}`);
          const baseLabel = managerFieldsSchema.find((f: any) => f.fieldId === fieldId)?.name
            || fieldId.replace('_', ' ');
          missingLabels.push(`Coach ${idx + 1}: ${baseLabel}`);
        }
      });
      customManagerFields.forEach((field: any) => {
        if (!field.required) return;
        const value = coach.info?.[field.fieldId];
        const empty = value == null || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && value.length === 0);
        if (empty) {
          errors.add(`${idx}.info.${field.fieldId}`);
          missingLabels.push(`Coach ${idx + 1}: ${field.name}`);
        }
      });
    });
    return { errors, missingLabels };
  };

  const handleSaveTeam = async () => {
    if (!team) return;
    if (!teamForm.name.trim()) {
      toast.error('Team name is required');
      return;
    }
    const { errors, missingLabels } = validateCoaches();
    if (errors.size > 0) {
      setCoachErrors(errors);
      toast.error(`Please fill out required fields: ${missingLabels.slice(0, 4).join(', ')}${missingLabels.length > 4 ? `, +${missingLabels.length - 4} more` : ''}`);
      return;
    }
    setCoachErrors(new Set());

    // Payment override validation (mirrors the registration Edit Team dialog).
    if (team.registrationTeam && regTeamForm.hasPaid) {
      const amt = regTeamForm.customPaidAmount !== '' ? parseFloat(regTeamForm.customPaidAmount) : NaN;
      if (!amt || amt <= 0) {
        toast.error("Custom paid amount is required when 'Has Paid' is enabled");
        return;
      }
      const origAmt = Number(_.get(team, 'registrationTeam.customPaidAmount') || 0);
      if (amt !== origAmt && !regTeamForm.paidNotes.trim()) {
        toast.error('A note is required when recording a custom payment');
        return;
      }
    }

    setIsSavingTeam(true);
    try {
      const divisionPayload: any = {
        _id: team._id,
        name: teamForm.name.trim(),
        active: teamForm.active,
      };
      await updateTeamDivision(divisionPayload, team.registrationTeam?._id || team.registrationTeam);

      const regTeam = team.registrationTeam;
      const regTeamId = regTeam && typeof regTeam === 'object' ? regTeam._id : regTeam;
      if (regTeamId) {
        const token = await getToken();
        const sanitizedManagers = coachForms.map((c) => ({
          ...(c._id && !String(c._id).startsWith('temp_') ? { _id: c._id } : {}),
          first_name: c.first_name || '',
          last_name: c.last_name || '',
          email: c.email || '',
          phone: c.phone || '',
          title: c.title || '',
          info: c.info || {},
        }));
        const amt = regTeamForm.hasPaid && regTeamForm.customPaidAmount !== ''
          ? parseFloat(regTeamForm.customPaidAmount) : null;
        const updatedRegTeam = {
          _id: regTeamId,
          teamManagers: sanitizedManagers,
          hasPaid: regTeamForm.hasPaid,
          paidNotes: regTeamForm.hasPaid ? regTeamForm.paidNotes : null,
          customPaidAmount: regTeamForm.hasPaid ? amt : null,
          customPaidDate: regTeamForm.hasPaid && amt && amt > 0 ? (regTeamForm.customPaidDate || new Date()) : null,
          // Merge (not replace) — the backend $set overwrites the whole config subdoc.
          config: { ..._.get(team, 'registrationTeam.config', {}), disableSync: regTeamForm.disableSync },
        };
        await updateTeamRegistrationFn(updatedRegTeam, token);
      }

      await getTeamDivisionById(teamId, { include: 'players,league,registrationTeam' });
      toast.success('Team saved');
    } catch (error) {
      toast.error('Failed to save team');
    }
    setIsSavingTeam(false);
  };

  const updateCoach = (index: number, field: string, value: string) => {
    setCoachForms((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
    if (value && coachErrors.has(`${index}.${field}`)) {
      setCoachErrors((prev) => {
        const next = new Set(prev);
        next.delete(`${index}.${field}`);
        return next;
      });
    }
  };

  const updateCoachInfo = (index: number, fieldId: string, value: any) => {
    setCoachForms((prev) => prev.map((c, i) => (
      i === index ? { ...c, info: { ...(c.info || {}), [fieldId]: value } } : c
    )));
    if (value && coachErrors.has(`${index}.info.${fieldId}`)) {
      setCoachErrors((prev) => {
        const next = new Set(prev);
        next.delete(`${index}.info.${fieldId}`);
        return next;
      });
    }
  };

  const addCoach = () => {
    setCoachForms([...coachForms, { first_name: '', last_name: '', email: '', phone: '', title: '', info: {} }]);
  };

  const removeCoach = (index: number) => {
    setCoachForms(coachForms.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <ListSkeleton />
      </PageContainer>
    );
  }

  if (!team) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Team not found.</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Go Back
        </Button>
      </div>
    );
  }

  const divisionName = team.division?.name || '';

  return (
    <PageContainer>
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" aria-label="Go back" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          className="flex-1"
          title={getTeamDisplayName(team)}
          description={divisionName ? <>Division: {divisionName}</> : undefined}
          actions={regTeamId ? (
            <Button variant="outline" size="sm" onClick={openResendDialog}>
              <Send className="h-3.5 w-3.5 mr-1" /> Resend registration invitation
            </Button>
          ) : undefined}
        />
      </div>

      {/* Resend registration invitation dialog */}
      <Dialog open={resendOpen} onOpenChange={setResendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resend registration invitation</DialogTitle>
            <DialogDescription>
              Email the team manager a link to sign up, complete registration, and pay.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="resend-email">Manager email</Label>
            <Input
              id="resend-email"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="manager@example.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendOpen(false)}>Cancel</Button>
            <Button
              onClick={handleResendInvite}
              disabled={isResending || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail.trim())}
            >
              {isResending ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Info */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Team Information</CardTitle>
          <Button size="sm" onClick={handleSaveTeam} disabled={isSavingTeam}>
            <Save className="h-3.5 w-3.5 mr-1" />
            {isSavingTeam ? 'Saving...' : 'Save Team'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Hide Team Name when the registration is configured for the
              individual/doubles flow (excludeTeamName=true) — the team gets
              an auto-generated zqtm_ name and showing an editable input
              would let admins overwrite it by mistake. */}
          {(() => {
            const excludeTeamName = !!registration?.config?.excludeTeamName;
            const gridCols = excludeTeamName ? 'md:grid-cols-2' : 'md:grid-cols-3';
            return (
              <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
                {!excludeTeamName && (
                  <div>
                    <Label htmlFor="teamName">Team Name</Label>
                    <Input
                      id="teamName"
                      value={teamForm.name}
                      onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                    />
                  </div>
                )}
                <div>
                  <Label>Seed</Label>
                  <div className="h-10 flex items-center text-sm">
                    {team?.seed === undefined || team?.seed === null || team?.seed === -1
                      ? <span className="text-muted-foreground">Unseeded</span>
                      : team.seed}
                    {scheduleHref && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        (managed on the{' '}
                        <a className="underline" href={scheduleHref} target="_blank" rel="noreferrer">
                          Division Schedule
                        </a>
                        )
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={teamForm.active}
                            onChange={(e) => setTeamForm({ ...teamForm, active: e.target.checked })}
                            className="rounded border-input"
                          />
                          Active
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Whether this team is part of the division. Active teams appear in standings,
                        schedules, and public listings; inactive teams stay hidden. This is normally
                        toggled automatically when an admin accepts the team's registration.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            );
          })()}

          {/* Coaches — hide when the registration doesn't collect coaches */}
          {registration?.config?.requireCoaches && (
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Coaches / Managers</h3>
              <Button size="sm" variant="outline" onClick={addCoach}>
                <Plus className="h-3 w-3 mr-1" /> Add Coach
              </Button>
            </div>
            {coachForms.length === 0 && (
              <p className="text-sm text-muted-foreground/60">No coaches added.</p>
            )}
            <div className="space-y-3">
              {coachForms.map((coach, idx) => {
                const errClass = (key: string) => coachErrors.has(`${idx}.${key}`)
                  ? 'border-destructive focus-visible:ring-destructive'
                  : '';
                return (
                <div key={coach._id || idx} className="border rounded p-3 bg-muted/50 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                    <div>
                      <Label className="text-xs">First Name <span className="text-destructive">*</span></Label>
                      <Input
                        value={coach.first_name || ''}
                        onChange={(e) => updateCoach(idx, 'first_name', e.target.value)}
                        placeholder="First"
                        className={errClass('first_name')}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Last Name <span className="text-destructive">*</span></Label>
                      <Input
                        value={coach.last_name || ''}
                        onChange={(e) => updateCoach(idx, 'last_name', e.target.value)}
                        placeholder="Last"
                        className={errClass('last_name')}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
                      <Input
                        type="email"
                        value={coach.email || ''}
                        onChange={(e) => updateCoach(idx, 'email', e.target.value)}
                        placeholder="Email"
                        className={errClass('email')}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Phone <span className="text-destructive">*</span></Label>
                      <Input
                        type="tel"
                        value={coach.phone || ''}
                        onChange={(e) => updateCoach(idx, 'phone', formatPhoneNumber(e.target.value))}
                        placeholder="(XXX) XXX-XXXX"
                        maxLength={14}
                        className={errClass('phone')}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Title <span className="text-destructive">*</span></Label>
                      <Select value={coach.title || ''} onValueChange={(v) => updateCoach(idx, 'title', v)}>
                        <SelectTrigger className={errClass('title')}>
                          <SelectValue placeholder="Select a title" />
                        </SelectTrigger>
                        <SelectContent>
                          {COACH_TITLE_OPTIONS.map((opt: string) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Button size="sm" variant="ghost" aria-label="Remove coach" onClick={() => removeCoach(idx)} className="text-destructive hover:text-destructive/80">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {customManagerFields.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {customManagerFields.map((field: any) => (
                        <div key={field.fieldId} className="space-y-1">
                          <Label className="text-xs">
                            {field.name}{field.required && <span className="text-destructive ml-1">*</span>}
                          </Label>
                          <RegistrationFieldInput
                            field={field}
                            value={coach.info?.[field.fieldId]}
                            onChange={(v) => updateCoachInfo(idx, field.fieldId, v)}
                            invalid={coachErrors.has(`${idx}.info.${field.fieldId}`)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Registration-team admin fields — only for teams that have a
              RegistrationTeam (payment override + sync flag), mirroring the
              registration "Edit Team" drawer. */}
          {regTeamId && (
            <div className="pt-4 border-t space-y-4">
              <h3 className="text-sm font-semibold">Payment &amp; Sync</h3>

              <div>
                <Label className="flex items-center gap-2 cursor-pointer w-fit">
                  <input
                    type="checkbox"
                    className="rounded border-input"
                    checked={regTeamForm.hasPaid}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setRegTeamForm((prev) => checked
                        ? { ...prev, hasPaid: true }
                        : { ...prev, hasPaid: false, customPaidAmount: '', paidNotes: '', customPaidDate: null });
                    }}
                  />
                  Has Paid
                </Label>
                <p className="text-xs text-muted-foreground mt-1">Manually mark this team as paid and record a custom amount.</p>
              </div>

              {regTeamForm.hasPaid && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                  <div>
                    <Label htmlFor="customPaidAmount">Custom Paid Amount ($) <span className="text-destructive">*</span></Label>
                    <Input
                      id="customPaidAmount"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={regTeamForm.customPaidAmount}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        setRegTeamForm((prev) => ({
                          ...prev,
                          customPaidAmount: v,
                          customPaidDate: parseFloat(v) > 0 ? (prev.customPaidDate || new Date()) : null,
                        }));
                      }}
                    />
                    {(!regTeamForm.customPaidAmount || parseFloat(regTeamForm.customPaidAmount) <= 0) && (
                      <p className="text-[11px] text-destructive mt-1">Required when "Has Paid" is enabled</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="paidNotes">Paid Notes <span className="text-destructive">*</span></Label>
                    <Input
                      id="paidNotes"
                      value={regTeamForm.paidNotes}
                      onChange={(e) => setRegTeamForm((prev) => ({ ...prev, paidNotes: e.target.value }))}
                      placeholder="What was this payment for? (required)"
                    />
                    {parseFloat(regTeamForm.customPaidAmount) > 0 && !regTeamForm.paidNotes.trim() && (
                      <p className="text-[11px] text-destructive mt-1">A note is required when recording a custom payment</p>
                    )}
                  </div>
                  {regTeamForm.customPaidDate && (
                    <div>
                      <Label>Payment Date</Label>
                      <div className="h-10 flex items-center text-sm text-muted-foreground">
                        {moment(regTeamForm.customPaidDate).format('MM/DD/YYYY hh:mm A')}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-end gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-input"
                          checked={regTeamForm.disableSync}
                          onChange={(e) => setRegTeamForm((prev) => ({ ...prev, disableSync: e.target.checked }))}
                        />
                        Disable Sync
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      When enabled, users will no longer be able to make changes to team information or player details.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registration details (payment / capacity / players) + player actions,
          mirroring the per-team detail on /admin/registration/:id */}
      {regTeamId && (
        <RegistrationTeamDetails
          paymentPlans={paymentPlans}
          regTeam={regTeam}
          payments={payments}
          registrationTeams={registrationTeams}
          divisions={registrationDivisions}
          registration={registration}
          profileFields={profileFields}
          userProfileByUser={userProfileByUser}
          userProfileByEmail={profileMaps.byEmail}
          playersActions={
            <TeamPlayerActions
              team={team}
              registration={registration}
              organization={organization}
              leagueId={leagueId}
              tournamentId={tournamentId}
              contestType={contestType}
              divisionId={helpers.getObjId(team?.division)}
              onChanged={refreshRegistrationData}
            />
          }
          renderPlayerActions={(player, { amountPaid } = {}) => (
            <div className="flex items-center justify-end gap-1">
              <PlayerEditDialog
                player={player}
                registration={registration}
                onSaved={refreshRegistrationData}
              />
              <PlayerRemoveDialog
                player={player}
                // Always pass the registrationTeam id — the denormalized
                // players mirror isn't soft-delete-aware and has to be cleaned.
                registrationTeamId={regTeamId}
                amountPaid={amountPaid}
                onRemoved={refreshRegistrationData}
              />
            </div>
          )}
          onSendFinishRegistration={async (playerEmails: string[]) => {
            if (!regTeamId) return;
            await sendFinishRegistrationEmails(regTeamId, playerEmails);
            refreshRegistrationData();
          }}
        />
      )}

      {/* Manually-added teams (no RegistrationTeam) get a plain roster card —
          the RegistrationTeamDetails card above only renders players hydrated
          off a registrationTeam, so without one there was NO roster UI at all
          (owner request 2026-09-21, Coconut Creek stats-only org). */}
      {!regTeamId && team && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Players</CardTitle>
            <TeamPlayerActions
              team={team}
              registration={registration}
              organization={organization}
              leagueId={leagueId}
              tournamentId={tournamentId}
              contestType={contestType}
              divisionId={helpers.getObjId(team?.division)}
              onChanged={() => getPlayersByTeamId(teamId)}
            />
          </CardHeader>
          <CardContent>
            {rosterPlayers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No players yet. Use <span className="font-medium">Create New Player</span> to build the roster.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-24">Jersey #</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rosterPlayers.map((player: any) => {
                    const displayName = _.get(player, 'info.first_name')
                      ? `${_.get(player, 'info.first_name', '')} ${_.get(player, 'info.last_name', '')}`.trim()
                      : (player.name || '—');
                    return (
                      <TableRow key={player._id}>
                        <TableCell className="font-medium">{displayName}</TableCell>
                        <TableCell>{player.number || ''}</TableCell>
                        <TableCell>{_.get(player, 'info.email') || player.email || ''}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <PlayerEditDialog
                              player={player}
                              registration={registration}
                              onSaved={() => getPlayersByTeamId(teamId)}
                            />
                            {/* No registrationTeamId: there is no denormalized
                                players mirror to clean on a manual team. */}
                            <PlayerRemoveDialog
                              player={player}
                              onRemoved={() => getPlayersByTeamId(teamId)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
